import { createPortal } from "react-dom";
import { CSSTransition } from "react-transition-group";
import { FocusScope, useOverlay, useOverlayPosition } from "react-aria";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import styles from "./Popover.module.scss";
import { PopoverProps } from "./Popover.types";

const ANIMATION_TIME = 300;

export const Popover = ({
  children,
  placement = "bottom start",
  offset = 4,
  isOpen,
  onClose,
  overlayRef,
  triggerRef,
  className,
  style,
  restoreFocus = true,
  hasFocusTrap,
  hasAutoFocus,
  shouldFlip = true,
}: PopoverProps) => {
  const localRef = useRef<HTMLDivElement>(null);
  const ref = overlayRef || localRef;
  const [triggerWidth, setTriggerWidth] = useState<undefined | number>(
    undefined
  );
  const [triggerHeight, setTriggerHeight] = useState<undefined | number>(
    undefined
  );

  useEffect(() => {
    setTriggerWidth(triggerRef.current?.getBoundingClientRect().width);
    setTriggerHeight(triggerRef.current?.getBoundingClientRect().height);
  }, [triggerRef]);

  useEffect(() => {
    const handleResize = () => {
      setTriggerWidth(triggerRef.current?.getBoundingClientRect().width);
      setTriggerHeight(triggerRef.current?.getBoundingClientRect().height);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [triggerRef]);

  const { overlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      shouldCloseOnBlur: false,
      isDismissable: true,
    },
    ref
  );

  const {
    overlayProps: { style: overlayStyle },
  } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: ref,
    placement: placement === "on-trigger" ? "bottom" : placement,
    offset: placement === "on-trigger" ? -(triggerHeight || 0) : offset,
    isOpen,
    shouldFlip,
  });

  const portalsElement = document.querySelector("#portals");

  return (
    <>
      {typeof window !== "undefined" &&
        portalsElement &&
        createPortal(
          <CSSTransition
            in={isOpen}
            timeout={ANIMATION_TIME}
            classNames={{
              enter: clsx(styles.popoverEnter, {
                [styles.popoverEnterOnTrigger]: placement === "on-trigger",
              }),
              enterActive: styles.popoverEnterActive,
              exit: styles.popoverExit,
              exitActive: styles.popoverExitActive,
            }}
            unmountOnExit
            nodeRef={ref}
          >
            <FocusScope
              restoreFocus={restoreFocus}
              autoFocus={hasAutoFocus}
              contain={hasFocusTrap}
            >
              <div
                {...overlayProps}
                className={clsx(styles.popover, className)}
                style={{
                  ...overlayStyle,
                  width: triggerWidth,
                  maxWidth: triggerWidth,
                  ...style,
                }}
                ref={ref}
              >
                {children}
              </div>
            </FocusScope>
          </CSSTransition>,
          portalsElement
        )}
    </>
  );
};
